import { useEffect, useState } from "react";
import { Formik } from "formik";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaEdit,
  FaRegCopy,
  FaTimes,
  FaPlus,
  FaTrash,
  FaSearch,
} from "react-icons/fa";
import * as Yup from "yup";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import ApiServices from "../../services/apiservices";
import { POST_STATUS } from "../../utils/constants";

const categories = [
  "All",
  "Public Announcement",
  "Tender Notice",
  "Service Level Shortfall",
  "Map-Meter-Refund",
  "Publications",
];

const PostTable = () => {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Pagination & Filtering state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  // Filter States
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [author, setAuthor] = useState("");

  const fetchPosts = async () => {
    try {
      setLoading(true);
      let endpoint = `/posts/fetch.php?page=${page}&limit=${limit}`;
      if (search.trim()) endpoint += `&search=${encodeURIComponent(search.trim())}`;
      if (selectedCategory && selectedCategory !== "All") endpoint += `&category=${encodeURIComponent(selectedCategory)}`;
      if (author.trim()) endpoint += `&author=${encodeURIComponent(author.trim())}`;

      const res = await ApiServices.getRequest(endpoint);
      let fetchedPosts = res.posts || [];

      // Client-side status filtering if selected status is PUBLISHED or DRAFT
      if (selectedStatus && selectedStatus !== "All") {
        fetchedPosts = fetchedPosts.filter(p => p.status === selectedStatus);
      }

      setPosts(fetchedPosts);
      setTotalPages(res.totalPages || 1);
      setTotalPosts(res.total || 0);
    } catch (error) {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [page, limit, search, selectedCategory, selectedStatus, author]);

  // Reset to page 1 whenever filters change
  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  // Unified function to handle status changes
  const handleToggleStatus = async (id) => {
    if (!window.confirm("Change the visibility status of this post?")) return;
    
    try {
      const res = await ApiServices.deleteRequest(`/posts/togglestatus.php?id=${id}`);
      if (res.success) {
        toast.success(res.message);
        fetchPosts();
        setIsModalOpen(false);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update status");
    }
  };

  const handleAddToLatestUpdates = async (id) => {
    if (!window.confirm("Kindly Confirm Action?")) return;
    
    try {
      const res = await ApiServices.putRequest(`/posts/togglelatestnews.php?id=${id}`);
      if (res.success) {
        toast.success(res.message);
        fetchPosts();
        setIsModalOpen(false);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update status");
    }
  };

  // Delete/Sponge post from database completely
  const handleDeletePost = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this post from the database? This action cannot be undone.")) return;

    try {
      const res = await ApiServices.deleteRequest(`/posts/delete.php?id=${id}`);
      if (res.success) {
        toast.success(res.message || "Post deleted successfully");
        fetchPosts();
        setIsModalOpen(false);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to delete post");
    }
  };

  const copyLink = (post) => {
    const url = `${window.location.origin}/${post.postLinkTitle}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  const validationSchema = Yup.object({
    title: Yup.string().required("Title required"),
    category: Yup.string().required("Category required"),
    createdAt: Yup.string().required("Creation date is required"),
    mainContent: Yup.string().when("category", {
      is: (val) => val !== "Tender Notice",
      then: (schema) => schema.required("Main content is required"),
    }),
  });

  const ImagePreview = ({ file, currentImageUrl }) => {
    const source = file instanceof File ? URL.createObjectURL(file) : currentImageUrl;
    if (!source) return <div className="w-32 h-32 mb-2 border rounded bg-gray-100 flex items-center justify-center text-xs text-gray-400">No Image</div>;

    return (
      <div className="relative w-32 h-32 mb-2 border rounded overflow-hidden bg-gray-50">
        <img src={source} className="w-full h-full object-cover" alt="Preview" />
      </div>
    );
  };

  const handleSubmit = async (values) => {    
    const formData = new FormData();
    Object.keys(values).forEach((key) => {
      const value = values[key];
      if (value === undefined || value === null) return;
      formData.append(key, value);
    });

    try {
      const res = await ApiServices.postRequest(`/posts/update.php?id=${selectedPost.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.success) {
        toast.success(res.message);
        setIsModalOpen(false);
        fetchPosts();
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  return (
    <main className="flex-1 bg-white p-6 overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold">Manage Posts</h2>
          {/* <p className="text-xs text-gray-500 mt-1">Total matching records: {totalPosts}</p> */}
        </div>
        <a href="/admin/post/new" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow-md transition-all">
          <FaPlus /> New Post
        </a>
      </div>

      {/* Filter Tab */}
      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Search Title */}
        <div className="relative">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Search Title</label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
              <FaSearch size={12} />
            </span>
            <input
              type="text"
              placeholder="Search title..."
              value={search}
              onChange={handleFilterChange(setSearch)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Category</label>
          <select
            value={selectedCategory}
            onChange={handleFilterChange(setSelectedCategory)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
          <select
            value={selectedStatus}
            onChange={handleFilterChange(setSelectedStatus)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="All">All Statuses</option>
            <option value={POST_STATUS.PUBLISHED}>Published</option>
            <option value={POST_STATUS.DRAFT}>Draft</option>
          </select>
        </div>

        {/* Author Filter */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Author</label>
          <input
            type="text"
            placeholder="Filter by author..."
            value={author}
            onChange={handleFilterChange(setAuthor)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Limit Per Page Selector */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Per Page Limit</label>
          <select
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="5">5 per page</option>
            <option value="10">10 per page</option>
            <option value="20">20 per page</option>
            <option value="50">50 per page</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="border-b px-4 py-3 text-center">Image</th>
              <th className="border-b px-4 py-3">Title</th>
              <th className="border-b px-4 py-3">Category</th>
              <th className="border-b px-4 py-3">Author</th>
              <th className="border-b px-4 py-3 text-center">Status</th>
              <th className="border-b px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {posts.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-12 text-gray-400">
                  No posts found matching your criteria.
                </td>
              </tr>
            ) : (
              posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-center">
                    {post.featuredImage ? (
                      <img src={post.featuredImage} className="w-12 h-12 mx-auto object-cover rounded shadow-sm" alt="" />
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">{post.title}</td>
                  <td className="px-4 py-3 text-gray-600">{post.category}</td>
                  <td className="px-4 py-3 text-gray-600">{post.author || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <button 
                      onClick={() => handleToggleStatus(post.id)}
                      className={`px-2.5 py-1 rounded text-xs font-semibold uppercase tracking-wider transition-all ${
                        post.status === POST_STATUS.PUBLISHED 
                        ? "bg-green-100 text-green-700 border border-green-200 hover:bg-green-200" 
                        : "bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200"
                      }`}
                    >
                      {post.status}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-3 justify-center items-center">
                      <button title="Edit Post" onClick={() => { setSelectedPost(post); setIsModalOpen(true); }}>
                        <FaEdit className="text-blue-600 hover:text-blue-800 text-lg" />
                      </button>
                      <button title="Copy Link" onClick={() => copyLink(post)}>
                        <FaRegCopy className="text-gray-600 hover:text-gray-800 text-lg" />
                      </button>
                      <button title="Delete Post" onClick={() => handleDeletePost(post.id)}>
                        <FaTrash className="text-red-500 hover:text-red-700 text-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Tab */}
      {!loading && totalPages > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 px-2">
          <div className="text-xs text-gray-500 font-medium">
            Showing page <span className="font-bold text-gray-700">{page}</span> of <span className="font-bold text-gray-700">{totalPages}</span> ({totalPosts} total entries)
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(p - 1, 1))}
              disabled={page === 1}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 disabled:opacity-40 hover:bg-gray-100 transition shadow-sm flex items-center gap-1"
            >
              <FiChevronLeft size={14} /> Previous
            </button>
            
            <div className="flex items-center gap-1 px-2">
              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= page - 1 && pageNum <= page + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                        page === pageNum
                          ? "bg-blue-600 text-white shadow"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  pageNum === page - 2 ||
                  pageNum === page + 2
                ) {
                  return <span key={pageNum} className="text-gray-400 px-1">...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setPage(p => Math.min(p + 1, totalPages))}
              disabled={page === totalPages || totalPages === 0}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 disabled:opacity-40 hover:bg-gray-100 transition shadow-sm flex items-center gap-1"
            >
              Next <FiChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal Component */}
      <AnimatePresence>
        {isModalOpen && selectedPost && (
          <motion.div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col relative">
              <button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 text-gray-500 hover:text-red-500 z-10">
                <FaTimes size={20} />
              </button>

              <div className="p-6 border-b">
                <h3 className="text-xl font-bold">Edit Post - {selectedPost.title}</h3>
              </div>

              <div className="p-6 overflow-y-auto">
                <Formik
                  enableReinitialize
                  initialValues={{
                    ...selectedPost,
                    introContent: selectedPost.introContent || "",
                    mainContent: selectedPost.mainContent || "",
                    createdAt: selectedPost.createdAt ? selectedPost.createdAt.slice(0, 16) : "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, errors, touched, setFieldValue, handleChange, handleSubmit }) => (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Post Title</label>
                          <input name="title" value={values.title} onChange={handleChange} className={`w-full border px-3 py-2 rounded outline-none ${errors.title && touched.title ? 'border-red-500' : 'border-gray-300'}`} />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
                          <select name="category" value={values.category} onChange={handleChange} className="w-full border border-gray-300 px-3 py-2 rounded outline-none">
                            {categories.filter(c => c !== "All").map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Post Date (Backdate / Edit)</label>
                          <input 
                            type="datetime-local" 
                            name="createdAt" 
                            value={values.createdAt} 
                            onChange={handleChange} 
                            className={`w-full border px-3 py-2 rounded outline-none ${errors.createdAt && touched.createdAt ? 'border-red-500' : 'border-gray-300'}`} 
                          />
                        </div>
                      </div>

                      {values.category !== "Tender Notice" && (
                        <>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Intro Content</label>
                            <textarea name="introContent" rows="2" value={values.introContent} onChange={handleChange} className="w-full border border-gray-300 px-3 py-2 rounded outline-none" />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Main Body Content</label>
                            <textarea name="mainContent" rows="5" value={values.mainContent} onChange={handleChange} className={`w-full border px-3 py-2 rounded outline-none ${errors.mainContent && touched.mainContent ? 'border-red-500' : 'border-gray-300'}`} />
                          </div>
                        </>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="flex flex-col">
                          <label className="text-sm font-semibold mb-2">Featured Image</label>
                          <ImagePreview file={values.featuredImage} currentImageUrl={selectedPost.featuredImage} />
                          <input type="file" className="text-xs" onChange={(e) => setFieldValue("featuredImage", e.currentTarget.files[0])} />
                        </div>
                      </div>

                      <div className="flex flex-wrap justify-between items-center pt-6 border-t mt-6 gap-4">
                        <div className="flex gap-2">
                          <button 
                            type="button" 
                            onClick={() => handleToggleStatus(selectedPost.id)} 
                            className={`px-4 py-2 rounded border font-medium transition-all ${
                              values.status === POST_STATUS.PUBLISHED 
                              ? "bg-amber-50 text-amber-700 border-amber-600 hover:bg-amber-600 hover:text-white" 
                              : "bg-green-50 text-green-700 border-green-600 hover:bg-green-600 hover:text-white"
                            }`}
                          >
                            {values.status === POST_STATUS.PUBLISHED ? "Unpublish (Draft)" : "Publish Now"}
                          </button>

                          <button 
                            type="button" 
                            onClick={() => handleAddToLatestUpdates(selectedPost.id)} 
                            className={`px-4 py-2 rounded border font-medium transition-all ${
                              values.addtoLatestUpdate === true
                              ? "bg-amber-50 text-amber-700 border-amber-600 hover:bg-amber-600 hover:text-white" 
                              : "bg-green-50 text-green-700 border-green-600 hover:bg-green-600 hover:text-white"
                            }`}
                          >
                             {values.addtoLatestUpdate === true ? "Remove from latest News" : "Add to latest News"}
                          </button>
                        </div>

                        <div className="flex gap-2">
                          <button 
                            type="button" 
                            onClick={() => handleDeletePost(selectedPost.id)} 
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded shadow-lg transition-all flex items-center gap-2"
                          >
                            <FaTrash /> Delete Post
                          </button>
                          
                          <button type="submit" className="px-8 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded shadow-lg transition-all">
                            Update Post
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default PostTable;